import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useRoleSwitcher } from '../features/auth/hooks/useRoleSwitcher.jsx';
import { useAuth } from '../features/auth/hooks/useAuth.jsx';
import { useSchool } from '../contexts/SchoolContext';
import menuService from '../services/menuService';
import { DEFAULT_MENU_ITEMS } from '../utils/constants';
import { ensureFeatureMenuItems } from '../services/menuItemSeeder';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase';
import { filterMenuItemsByCentralDomainSurfaces } from '../services/domain/centralDefinitions';

// LocalStorage cache configuration (shared across tabs)
const CACHE_KEY = 'swan_menu_items';
// No TTL - real-time listener handles data freshness
// Cache persists across tabs and browser sessions (cleared on logout)

// All possible roles in the system
const ALL_ROLES = ['teacher', 'admin', 'parent', 'dev'];

/**
 * Save menu items to localStorage - both flat and nested (shared across tabs)
 * Prevents flash of unnested menu by caching the final hierarchical structure
 * @param {Array} flatItems - Flat menu items from Firestore
 */
const saveToSessionStorage = (flatItems) => {
  try {
    // Build nested hierarchies for ALL roles upfront
    const hierarchy = menuService.buildHierarchy(flatItems);
    const nestedByRole = {};

    ALL_ROLES.forEach(role => {
      nestedByRole[role] = menuService.filterHierarchyByRole(hierarchy, role);
    });

    const cacheEntry = {
      flat: flatItems,       // Store flat items for admin menu editor
      nested: nestedByRole,  // Store nested menus per role for display
      timestamp: Date.now()
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
    console.log('[useMenuItems] 💾 Saved menus (flat + nested for all roles) to localStorage (shared across tabs)');
  } catch (error) {
    // Handle quota exceeded or other errors - fail silently
    if (error.name === 'QuotaExceededError') {
      console.warn('[useMenuItems] localStorage quota exceeded');
    }
  }
};

/**
 * Load NESTED menu items from localStorage for a specific role (shared across tabs)
 * Returns pre-computed hierarchical menu - no flash, instant display
 * @param {string} role - Current role
 * @returns {Array|null} Cached nested menu items for role or null if not found
 */
const loadFromSessionStorage = (role) => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);

    // No expiration check - real-time listener keeps data fresh
    const cacheAge = Date.now() - cacheEntry.timestamp;
    const nestedMenu = cacheEntry.nested?.[role];

    if (nestedMenu) {
      console.log('[useMenuItems] 📦 NESTED CACHE HIT from localStorage', {
        role,
        cacheAgeSec: (cacheAge / 1000).toFixed(1),
        itemCount: nestedMenu.length
      });
      return nestedMenu;
    }

    return null;
  } catch (error) {
    // Handle invalid JSON - clear corrupted cache
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (e) {
      // Ignore cleanup errors
    }
    return null;
  }
};

/**
 * Load FLAT menu items from localStorage (for admin menu editor, shared across tabs)
 * @returns {Array|null} Cached flat menu items or null if not found
 */
const loadFlatFromSessionStorage = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const cacheEntry = JSON.parse(cached);

    // No expiration check - real-time listener keeps data fresh
    return cacheEntry.flat || null;
  } catch (error) {
    return null;
  }
};

/**
 * Custom hook for managing menu items with role-based filtering
 * Handles loading, caching (localStorage + real-time), and filtering of menu items
 */
const useMenuItems = () => {
  const { user } = useAuth();
  const userId = user?.uid;
  const { currentRole } = useRoleSwitcher();
  const { loading: schoolLoading, menuItems: schoolMenuItems } = useSchool();

  // Load NESTED menu from cache SYNCHRONOUSLY before first render (instant display, no flash)
  const [menuItems, setMenuItems] = useState(() => {
    // PRIORITY 1: Try SchoolContext menuItems (from getUserInitData Cloud Function)
    if (schoolMenuItems && schoolMenuItems.length > 0) {
      console.log('[useMenuItems] 🎯 Using menu items from SchoolContext');
      const hierarchy = menuService.buildHierarchy(schoolMenuItems);
      return menuService.filterHierarchyByRole(hierarchy, currentRole || 'teacher');
    }

    // PRIORITY 2: Try localStorage cache (shared across tabs)
    const cached = loadFromSessionStorage(currentRole || 'teacher');
    if (cached && cached.length > 0) {
      return cached;
    }

    // FALLBACK: No cache available, start with empty menu
    console.log('[useMenuItems] ⚠️ No cached menu - starting empty');
    return [];
  });

  // If we have SchoolContext menuItems or cache, we're not loading
  const [loading, setLoading] = useState(() => {
    if (schoolMenuItems && schoolMenuItems.length > 0) {
      return false;
    }
    const cached = loadFromSessionStorage(currentRole || 'teacher');
    return !cached || cached.length === 0;
  });

  const [error, setError] = useState(null);

  /**
   * Load menu items from the service
   */
  const loadMenuItems = async (forceRefresh = false) => {
    try {
      setLoading(!hasSavedMenu.current);
      setError(null);

      // Menu items are global - no schoolId needed
      const items = await menuService.getAllMenuItems(null, forceRefresh);

      if (items.length === 0) {
        // Initialize default menu items (global - no schoolId)
        await menuService.initializeDefaultMenuItems(DEFAULT_MENU_ITEMS, null);

        // Reload after initialization with fresh data
        const newItems = await menuService.getAllMenuItems(null, true);
        if (newItems.length > 0) {
          setMenuItems(newItems);
        } else {
          console.log('[useMenuItems] ⚠️ Failed to initialize menu items');
          setMenuItems([]);
          setError('Failed to initialize menu. Please refresh.');
        }
      } else {
        setMenuItems(items);
      }
    } catch (err) {
      console.error('[useMenuItems] Error loading menu items:', err);
      setError(err.message || 'Failed to load menu permissions. Please refresh.');
      // Fail closed - no fallback to stale defaults
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  // refreshMenuItems and loadMenuItems are memoized below in the return value

  // Cache for storing nested menus per role (loaded from sessionStorage or Firestore)
  const nestedMenuCache = useRef({});

  // Keep a ref to currentRole so the Firestore listener callback always uses the latest value
  const currentRoleRef = useRef(currentRole);
  useEffect(() => {
    currentRoleRef.current = currentRole;
  }, [currentRole]);

  // Update menuItems when role changes - load from cache
  useEffect(() => {
    // Check if we have cached nested menu for this role
    if (nestedMenuCache.current[currentRole]) {
      setMenuItems(nestedMenuCache.current[currentRole]);
      setLoading(false);
      return;
    }

    // Try to load from localStorage (shared across tabs)
    const cached = loadFromSessionStorage(currentRole);
    if (cached && cached.length > 0) {
      nestedMenuCache.current[currentRole] = cached;
      setMenuItems(cached);
      setLoading(false);
    }
  }, [currentRole]);

  // Store flat items for admin menu editor
  const [flatMenuItems, setFlatMenuItems] = useState(() => {
    const cached = loadFlatFromSessionStorage();
    if (!cached) {
      console.log('[useMenuItems] ⚠️ No cached flat menu - starting empty');
    }
    return cached || [];
  });
  const hasSavedMenu = useRef(false);
  hasSavedMenu.current = flatMenuItems.length > 0 || Boolean(schoolMenuItems?.length);

  /**
   * Get all menu items (unfiltered, for admin use) (memoized)
   * Returns flat structure - SortableTreeMenu will build its own hierarchy
   */
  const allMenuItemsMemo = useMemo(() => {
    return menuService.removeDuplicates(flatMenuItems);
  }, [flatMenuItems]);

  // Process schoolMenuItems from Cloud Function (one-time initialization)
  const schoolMenuItemsProcessed = useRef(false);
  useEffect(() => {
    if (schoolLoading || !schoolMenuItems || schoolMenuItems.length === 0) return;
    if (schoolMenuItemsProcessed.current) return;
    schoolMenuItemsProcessed.current = true;

    console.log('[useMenuItems] 📦 Using Cloud Function data for initial state');
    saveToSessionStorage(schoolMenuItems);
    setFlatMenuItems(schoolMenuItems);

    const hierarchy = menuService.buildHierarchy(schoolMenuItems);
    ALL_ROLES.forEach(role => {
      nestedMenuCache.current[role] = menuService.filterHierarchyByRole(hierarchy, role);
    });

    setMenuItems(nestedMenuCache.current[currentRoleRef.current]);
    setLoading(false);
  }, [schoolMenuItems, schoolLoading]);

  // Set up real-time listener for menu updates (stable - does NOT depend on currentRole)
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (schoolLoading) {
      setLoading(true);
      console.log('[useMenuItems] ⏸️ Waiting for school context to load...');
      return;
    }

    // A subscription refresh must not tear down a route already authorised
    // by its saved menu. The next snapshot updates that menu in place.
    setLoading(!hasSavedMenu.current);
    console.log('[useMenuItems] 📡 Setting up Firestore listener for real-time updates');

    const menuQuery = query(collection(db, 'menuItems'));

    const unsubscribe = onSnapshot(
      menuQuery,
      (snapshot) => {
        try {
          if (snapshot.metadata.hasPendingWrites) {
            console.log('[useMenuItems] ⏸️ Snapshot has pending writes, skipping update');
            return;
          }

          const items = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          if (items.length === 0) {
            setLoading(false);
            setError(null);
          } else {
            const sortedItems = menuService.removeDuplicates(items);

            ensureFeatureMenuItems(sortedItems).catch(err =>
              console.error('[useMenuItems] Auto-seed failed:', err)
            );

            saveToSessionStorage(sortedItems);
            setFlatMenuItems(sortedItems);

            // Build and cache nested hierarchies for ALL roles
            const hierarchy = menuService.buildHierarchy(sortedItems);
            ALL_ROLES.forEach(role => {
              nestedMenuCache.current[role] = menuService.filterHierarchyByRole(hierarchy, role);
            });

            // Use ref to always get the CURRENT role (no stale closure)
            setMenuItems(nestedMenuCache.current[currentRoleRef.current]);

            setLoading(false);
            setError(null);
          }
          console.log('[useMenuItems] 📦 Listener update (full snapshot):', items.length, 'items');
        } catch (err) {
          console.error('[useMenuItems] Error processing snapshot:', err);
          setError(err?.message || 'Error loading menu');
          setLoading(false);
        }
      },
      (error) => {
        console.error('[useMenuItems] Subscription error:', error);
        setError(error?.message || 'Subscription error');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [schoolLoading, userId]); // A refreshed object for the same account is not a new session.

  // Memoize stable function references
  const stableRefreshMenuItems = useCallback(() => {
    console.log('[useMenuItems] Force refreshing menu items');
    return loadMenuItems(true);
  }, []);

  const stableLoadMenuItems = useCallback((forceRefresh = false) => {
    return loadMenuItems(forceRefresh);
  }, []);

  const governedMenuItems = useMemo(
    () => filterMenuItemsByCentralDomainSurfaces(menuItems),
    [menuItems],
  );

  // Memoize return value to prevent unnecessary context re-renders
  return useMemo(() => ({
    menuItems: governedMenuItems,
    allMenuItems: allMenuItemsMemo,
    loading,
    error,
    refreshMenuItems: stableRefreshMenuItems,
    loadMenuItems: stableLoadMenuItems
  }), [governedMenuItems, allMenuItemsMemo, loading, error, stableRefreshMenuItems, stableLoadMenuItems]);
};

export default useMenuItems;
